import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, m } from 'motion/react'
import { X } from 'lucide-react'
import { useT } from '../lib/i18n.tsx'
import { cn, duration, usePhone } from './util.ts'

export interface SheetProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  /** 'right' slides in from the side (a bottom sheet on phones), 'center' is a dialog. */
  side?: 'right' | 'bottom' | 'center'
  size?: 'sm' | 'md' | 'lg'
  footer?: ReactNode
  /** Hide the header row (the content draws its own). */
  bare?: boolean
  children?: ReactNode
  className?: string
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'

/** Modal panel with focus trap, Escape and scrim: <Sheet open title="Shortcuts" onClose={…}>…</Sheet>. */
export function Sheet({ open, onClose, title, description, side = 'right', size = 'md', footer, bare, children, className }: SheetProps) {
  const t = useT()
  const phone = usePhone()
  const where = side === 'right' && phone ? 'bottom' : side
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descId = useId()
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    if (!open) return
    const before = document.activeElement as HTMLElement | null
    const timer = window.setTimeout(() => {
      const first = panel.current?.querySelector<HTMLElement>('[data-autofocus]') ?? panel.current
      first?.focus()
    }, 10)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        close.current()
      }
      if (e.key !== 'Tab' || !panel.current) return
      const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null)
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey, true)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('keydown', onKey, true)
      document.body.style.overflow = overflow
      before?.focus?.()
    }
  }, [open])

  const d = duration('base')
  const motion = {
    right: { initial: { x: '100%' }, animate: { x: 0 }, exit: { x: '100%' } },
    bottom: { initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' } },
    center: { initial: { opacity: 0, y: 10, scale: 0.98 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: 6, scale: 0.98 } },
  }[where]

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-sheet>
          <m.div
            className="absolute inset-0 bg-scrim backdrop-blur-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: d }}
            onClick={() => close.current()}
          />
          <div
            className={cn(
              'pointer-events-none absolute inset-0 flex',
              where === 'right' && 'justify-end',
              where === 'bottom' && 'items-end',
              where === 'center' && 'items-center justify-center p-4',
            )}
          >
            <m.div
              ref={panel}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              aria-describedby={description ? descId : undefined}
              tabIndex={-1}
              {...motion}
              transition={{ duration: d, ease: [0.16, 1, 0.3, 1] }}
              className={cn(
                'pointer-events-auto flex flex-col bg-overlay text-ink shadow-modal outline-none',
                where === 'right' && 'h-full w-full border-l border-line',
                where === 'right' && (size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-sheet'),
                where === 'bottom' && 'max-h-sheet w-full rounded-t-2xl border-t border-line pb-safe',
                where === 'center' && 'max-h-full w-full rounded-xl ring-1 ring-line-strong',
                where === 'center' && (size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-dialog' : 'max-w-md'),
                className,
              )}
            >
              {where === 'bottom' && <span aria-hidden className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-line-strong" />}
              <div className={cn('flex items-start justify-between gap-4 px-5 pt-4', bare && 'sr-only')}>
                <div className="min-w-0">
                  <h2 id={titleId} className="font-display text-lg font-semibold text-ink">
                    {title}
                  </h2>
                  {description && (
                    <p id={descId} className="mt-0.5 text-sm text-ink-muted">
                      {description}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => close.current()}
                  aria-label={t('common.close')}
                  className="-mr-1.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-ink-faint transition hover:bg-hover hover:text-ink"
                >
                  <X size={16} strokeWidth={1.75} aria-hidden />
                </button>
              </div>
              {/* Focusable so the keyboard can scroll a body whose content has nothing focusable (axe scrollable-region-focusable). */}
              <div tabIndex={0} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
              {footer && <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
            </m.div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
